import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Wrap,
  WrapItem
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import { CopyButton } from '../../../../components/ActionButton'
import { CharCount } from '../../../../components/CharCount'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FormField } from '../../../../components/FormField'
import { FormModal } from '../../../../components/FormModal'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { LoadingBlock } from '../../../../components/Loading'
import { RichTextField } from '../../../../components/RichTextField'
import { ISelectOption, SearchableSelect } from '../../../../components/Select'
import { useAppToast, WEBSITE_URL } from '../../../../helpers'
import { IBlogPostFields, BlogPostService } from '../../../../services/BlogPost/BlogPostService'
import { BlogPostDraft, SeoKeyword, KnowledgeArea } from '../../../../services/BlogPost/types'
import { DraftsModal } from './DraftsModal'
import { PostStatusControls } from './PostStatusControls'

interface IProps {
  isOpen: boolean
  onClose: () => void
  /** Chamado depois de salvar o post, para a listagem recarregar. Cancelar não recarrega nada. */
  onSaved: () => void
  /** Post em edição. Sem ele o modal cadastra um post novo. */
  blogPostId?: number
}

/**
 * Limites de caracteres — os mesmos `max` do `BlogPostCreateRequest`/`BlogPostUpdateRequest` da
 * API. O `maxLength` e o contador leem daqui, para não voltarem a divergir (o Título SEO aceitava
 * 255 aqui e a API recusava acima de 60).
 */
const LIMITS = {
  title: 255,
  header: 255,
  seo_title: 60,
  meta_description: 255,
  seo_keyword: 100,
  seo_slug: 255,
  image_alt_text: 255,
  schema_headline: 110,
  schema_description: 255
}

const baseForm: IBlogPostFields = {
  title: '',
  header: '',
  body: '',
  image: null,
  is_active: true,
  scheduled_at: null,
  knowledge_areas: [],
  seo_title: '',
  meta_description: '',
  seo_keywords: [],
  seo_slug: '',
  image_alt_text: '',
  schema_type: 'BlogPosting',
  schema_headline: '',
  schema_description: ''
}

const baseFormErrors = {
  title: '',
  header: '',
  body: '',
  image: '',
  knowledge_areas: '',
  seo_title: '',
  meta_description: '',
  seo_keywords: '',
  seo_slug: '',
  image_alt_text: '',
  schema_type: '',
  schema_headline: '',
  schema_description: ''
}

const FIELD_ORDER = [
  'title',
  'header',
  'knowledge_areas',
  'image',
  'body',
  'seo_title',
  'meta_description',
  'seo_keywords',
  'seo_slug',
  'image_alt_text',
  'schema_type',
  'schema_headline',
  'schema_description'
] as const

/**
 * Compara campo a campo — a imagem por identidade (um `File` não serializa), o resto por valor.
 * Campo a campo, e não o objeto inteiro serializado, para não depender da ordem das chaves.
 */
const isSameForm = (a: IBlogPostFields, b: IBlogPostFields) =>
  (Object.keys(baseForm) as (keyof IBlogPostFields)[]).every((key) =>
    key === 'image' ? a.image === b.image : JSON.stringify(a[key]) === JSON.stringify(b[key])
  )

/** Palavras-chave de um texto separado por vírgula, sem as vazias. */
const parseKeywords = (text: string) =>
  text
    .split(',')
    .map((kw) => kw.trim())
    .filter(Boolean)

/** Junta palavras-chave novas às que já estão no form, sem repetir e em ordem alfabética. */
const mergeKeywords = (current: string[], added: string[]) =>
  Array.from(new Set([...current, ...added])).sort((a, b) => a.localeCompare(b))

export function PostsFormModal({ isOpen, onClose, onSaved, blogPostId }: IProps) {
  const service = new BlogPostService()
  const [saving, setSaving] = useState(false)
  const toast = useAppToast()
  const [form, setForm] = useState<IBlogPostFields>()
  /** Estado do formulário ao abrir (ou ao carregar/salvar um rascunho), para saber se há o que perder ao fechar. */
  const [initialForm, setInitialForm] = useState<IBlogPostFields>()
  const [knowledgeAreas, setKnowledgeAreas] = useState<KnowledgeArea[]>([])
  const [formErrors, setFormErrors] = useState(baseFormErrors)
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null)
  const [existingImagePath, setExistingImagePath] = useState<string | null>(null)
  const [tempKeyword, setTempKeyword] = useState('')
  const [isDraftsOpen, setIsDraftsOpen] = useState(false)
  const [isDiscardOpen, setIsDiscardOpen] = useState(false)
  const [postSlug, setPostSlug] = useState<string | null>(null)
  /** Rascunho de onde o post veio: é excluído depois que o post é publicado. */
  const [draftId, setDraftId] = useState<number | null>(null)

  const titleRef = useRef<HTMLInputElement>(null)
  const headerRef = useRef<HTMLInputElement>(null)
  const knowledgeAreasRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const seoTitleRef = useRef<HTMLInputElement>(null)
  const metaDescriptionRef = useRef<HTMLTextAreaElement>(null)
  const seoKeywordsRef = useRef<HTMLInputElement>(null)
  const seoSlugRef = useRef<HTMLInputElement>(null)
  const imageAltTextRef = useRef<HTMLInputElement>(null)
  const schemaTypeRef = useRef<HTMLDivElement>(null)
  const schemaHeadlineRef = useRef<HTMLInputElement>(null)
  const schemaDescriptionRef = useRef<HTMLTextAreaElement>(null)

  const fieldRefs: Record<(typeof FIELD_ORDER)[number], React.RefObject<HTMLElement>> = {
    title: titleRef,
    header: headerRef,
    knowledge_areas: knowledgeAreasRef,
    image: imageRef,
    body: bodyRef,
    seo_title: seoTitleRef,
    meta_description: metaDescriptionRef,
    seo_keywords: seoKeywordsRef,
    seo_slug: seoSlugRef,
    image_alt_text: imageAltTextRef,
    schema_type: schemaTypeRef,
    schema_headline: schemaHeadlineRef,
    schema_description: schemaDescriptionRef
  }

  useEffect(() => {
    let cancelled = false

    if (isOpen) {
      setSaving(false)
      setFormErrors(baseFormErrors)
      setExistingImageUrl(null)
      setExistingImagePath(null)
      setPostSlug(null)
      setTempKeyword('')
      setDraftId(null)

      service.getKnowledgeAreas().then((areas) => {
        if (!cancelled) setKnowledgeAreas(areas)
      })

      if (blogPostId) {
        // Sem `form` o corpo mostra o carregamento, em vez de o modal só aparecer quando a
        // requisição volta.
        setForm(undefined)
        setInitialForm(undefined)
        service
          .get(blogPostId)
          .then((response) => {
            if (cancelled) return
            const loaded: IBlogPostFields = {
              title: response.title,
              header: response.header,
              body: response.body,
              image: null,
              image_alt_text: response.image_alt_text ?? '',
              is_active: response.is_active,
              scheduled_at: response.scheduled_at ? response.scheduled_at.slice(0, 16) : null,
              knowledge_areas: response.knowledge_areas.map((ka) => ka.id),
              seo_title: response.seo_title ?? '',
              meta_description: response.meta_description ?? '',
              seo_keywords: Array.isArray(response.seo_keywords)
                ? response.seo_keywords.map((kw: SeoKeyword) => kw.keyword)
                : [],
              seo_slug: response.seo_slug ?? '',
              schema_type: response.schema_markup?.schema_type || 'BlogPosting',
              schema_headline: response.schema_markup?.schema_headline || '',
              schema_description: response.schema_markup?.schema_description || ''
            }
            setForm(loaded)
            setInitialForm(loaded)

            if (response.image_url) {
              setExistingImageUrl(response.image_url)
            }

            setPostSlug(response.slug ?? null)
          })
          .catch((error) => {
            if (cancelled) return
            toast.error('Erro ao carregar o post', error)
            onClose()
          })
      } else {
        setForm(baseForm)
        setInitialForm(baseForm)
      }
    } else {
      setIsDraftsOpen(false)
      setIsDiscardOpen(false)
    }

    return () => {
      cancelled = true
    }
  }, [isOpen, blogPostId])

  const updateForm = (patch: Partial<IBlogPostFields>) => setForm((prev) => (prev ? { ...prev, ...patch } : prev))

  const isDirty = !!form && !!initialForm && (!isSameForm(form, initialForm) || tempKeyword.trim() !== '')

  // Esc, clique fora, X e Cancelar passam todos por aqui: fechar com alterações pede confirmação.
  const requestClose = () => {
    if (saving) return
    if (isDirty) {
      setIsDiscardOpen(true)
      return
    }
    onClose()
  }

  /**
   * Junta ao form as palavras-chave digitadas (separadas por vírgula). Roda no Enter e ao sair do
   * campo — antes, o que ficava digitado sem Enter se perdia no salvar.
   *
   * O limite da API é por palavra-chave, não pelo texto digitado: a que passa dele fica no campo
   * para ser encurtada, em vez de virar erro só no salvar.
   */
  const addKeywords = () => {
    const keywords = parseKeywords(tempKeyword)
    const valid = keywords.filter((kw) => kw.length <= LIMITS.seo_keyword)
    const tooLong = keywords.filter((kw) => kw.length > LIMITS.seo_keyword)

    if (valid.length > 0) {
      setForm((prev) => (prev ? { ...prev, seo_keywords: mergeKeywords(prev.seo_keywords, valid) } : prev))
    }

    if (tooLong.length > 0) {
      toast.warning('Palavra-chave muito longa', `Cada palavra-chave aceita até ${LIMITS.seo_keyword} caracteres.`)
    }

    setTempKeyword(tooLong.join(', '))
  }

  /** O form com o que ficou digitado no campo de palavras-chave, mesmo sem Enter. */
  const withPendingKeywords = (current: IBlogPostFields): IBlogPostFields => {
    const pending = parseKeywords(tempKeyword)

    return pending.length ? { ...current, seo_keywords: mergeKeywords(current.seo_keywords, pending) } : current
  }

  const removeKeyword = (keyword: string) =>
    setForm((prev) => (prev ? { ...prev, seo_keywords: prev.seo_keywords.filter((kw) => kw !== keyword) } : prev))

  const handleSave = async () => {
    if (!form) return
    setSaving(true)

    const payload = withPendingKeywords(form)

    const saveFunction = blogPostId ? service.update(blogPostId, payload) : service.create(payload)

    saveFunction
      .then(async () => {
        toast.success('Post salvo com sucesso')

        if (draftId) {
          // O post já foi salvo: uma falha aqui só deixa o rascunho na lista.
          await service.deleteDraft(draftId).catch(() => {
            toast.warning('Não foi possível excluir o rascunho usado', 'Exclua-o manualmente em Rascunhos.')
          })
        }

        onSaved()
        onClose()
      })
      .catch((response) => {
        const errors = response.response?.data?.errors

        if (errors) {
          setFormErrors(errors)

          const firstErrorField = FIELD_ORDER.find((field) => errors[field])
          const target = firstErrorField ? fieldRefs[firstErrorField]?.current : null

          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' })
            if (typeof target.focus === 'function') {
              // preventScroll avoids the browser's default instant scroll-on-focus,
              // which would otherwise cut off the smooth scrollIntoView animation above.
              target.focus({ preventScroll: true })
            }
          } else {
            const rawMessage = firstErrorField ? errors[firstErrorField] : Object.values(errors)[0]
            toast.error('Erro de validação', Array.isArray(rawMessage) ? rawMessage[0] : rawMessage)
          }
          return
        }

        toast.error(blogPostId ? 'Erro ao editar post' : 'Erro ao adicionar post')
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const handleSaveDraft = async () => {
    if (!form) return
    setSaving(true)

    const draftForm = withPendingKeywords(form)
    const { image, ...formWithoutImage } = draftForm

    const resolveImage = async (): Promise<{ path: string | null; url: string | null }> => {
      if (image instanceof File) {
        try {
          const uploaded = await service.uploadDraftImage(image)
          return { path: uploaded.path, url: uploaded.url }
        } catch {
          return { path: existingImagePath, url: existingImageUrl }
        }
      }
      return { path: existingImagePath, url: existingImageUrl }
    }

    resolveImage()
      .then(({ path, url }) =>
        service.createDraft({
          title: draftForm.title || null,
          body: draftForm.body || null,
          form_data: { ...formWithoutImage, image_url: url, image_path: path }
        })
      )
      .then((draft) => {
        toast.success('Rascunho salvo com sucesso')
        // Publicar depois exclui este rascunho, como acontece com um rascunho aberto pela lista.
        setDraftId(draft.id)
        // O conteúdo está guardado no rascunho: fechar agora não perde nada.
        setForm(draftForm)
        setInitialForm(draftForm)
        setTempKeyword('')
      })
      .catch(() => {
        toast.error('Erro ao salvar rascunho')
      })
      .finally(() => setSaving(false))
  }

  const handleSelectDraft = async (draft: BlogPostDraft) => {
    const data = draft.form_data
    const loaded: IBlogPostFields = {
      title: data.title || '',
      header: data.header || '',
      body: data.body || '',
      image: null,
      image_alt_text: data.image_alt_text || '',
      is_active: data.is_active ?? true,
      scheduled_at: data.scheduled_at || null,
      knowledge_areas: data.knowledge_areas || [],
      seo_title: data.seo_title || '',
      meta_description: data.meta_description || '',
      seo_keywords: data.seo_keywords || [],
      seo_slug: data.seo_slug || '',
      schema_type: data.schema_type || 'BlogPosting',
      schema_headline: data.schema_headline || '',
      schema_description: data.schema_description || ''
    }
    setForm(loaded)
    setInitialForm(loaded)
    setTempKeyword('')
    setFormErrors(baseFormErrors)
    setDraftId(draft.id)
    setExistingImageUrl(data.image_url || null)
    setExistingImagePath(data.image_path || null)

    // Rebuild File from MinIO via backend proxy so the next "Salvar" persists the image
    if (data.image_path) {
      try {
        const blob = await service.getDraftImageBlob(draft.id)
        const extension = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg')
        const file = new File([blob], `draft-image.${extension}`, { type: blob.type })
        // A imagem veio do rascunho, não do usuário: entra também no estado inicial.
        setForm((prev) => (prev ? { ...prev, image: file } : prev))
        setInitialForm((prev) => (prev ? { ...prev, image: file } : prev))
      } catch {
        // Silently ignore: preview still works via existingImageUrl
      }
    }
  }

  const schemaTypeOptions: ISelectOption<'Article' | 'BlogPosting' | 'NewsArticle'>[] = [
    { label: 'Artigo', value: 'Article' },
    { label: 'Post de Blog', value: 'BlogPosting' },
    { label: 'Artigo de Notícias', value: 'NewsArticle' }
  ]

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={requestClose}
        title={
          <Flex align="center" justify="space-between" pr={8}>
            <Text>{blogPostId ? 'Editar' : 'Cadastrar'} Post do Blog</Text>
            {!blogPostId && (
              <Button size="sm" variant="outline" colorScheme="blue" onClick={() => setIsDraftsOpen(true)}>
                Rascunhos
              </Button>
            )}
          </Flex>
        }
        size="editor"
        footer={
          <>
            <Button colorScheme="magenta" mr={3} onClick={handleSave} isLoading={saving} isDisabled={!form}>
              Salvar
            </Button>
            {!blogPostId && (
              <Button
                colorScheme="gray"
                mr={3}
                onClick={handleSaveDraft}
                isDisabled={saving || !form}
                variant="outline"
              >
                Salvar como Rascunho
              </Button>
            )}
            <Button variant="ghost" onClick={requestClose} isDisabled={saving}>
              Cancelar
            </Button>
          </>
        }
      >
        {!form ? (
          <LoadingBlock entity="post" />
        ) : (
          <>
            <Flex direction="row" gap={3}>
              <Flex direction="column" gap={4} w="75%">
                <Box>
                  <FormField label="Título" htmlFor="title" error={formErrors.title} isRequired>
                    <Input
                      ref={titleRef}
                      value={form.title}
                      onChange={(e) => updateForm({ title: e.target.value })}
                      id="title"
                      disabled={saving}
                      maxLength={LIMITS.title}
                    />
                    <CharCount value={form.title} max={LIMITS.title} />
                  </FormField>
                </Box>
                {postSlug && (
                  <Box>
                    <FormField label="URL do artigo" htmlFor="post_url">
                      <Flex gap={2}>
                        <Input id="post_url" value={`${WEBSITE_URL}/artigos/${postSlug}`} isReadOnly bg="gray.50" />
                        <CopyButton
                          value={`${WEBSITE_URL}/artigos/${postSlug}`}
                          entity="URL"
                          feminine
                          showLabel
                          size="md"
                          minW="100px"
                          flexShrink={0}
                          mr={0}
                        />
                      </Flex>
                    </FormField>
                  </Box>
                )}
                <Box>
                  <FormField label="Cabeçalho" htmlFor="header" error={formErrors.header} isRequired>
                    <Input
                      ref={headerRef}
                      value={form.header}
                      onChange={(e) => updateForm({ header: e.target.value })}
                      id="header"
                      disabled={saving}
                      maxLength={LIMITS.header}
                    />
                    <CharCount value={form.header} max={LIMITS.header} />
                  </FormField>
                </Box>

                <Box ref={knowledgeAreasRef} tabIndex={-1}>
                  <FormField label="Áreas de Conhecimento" error={formErrors.knowledge_areas} isRequired>
                    <SearchableSelect<number>
                      isMulti
                      value={form.knowledge_areas}
                      options={knowledgeAreas.map((area) => ({ label: area.name, value: area.id }))}
                      onChange={(areaIds) => updateForm({ knowledge_areas: areaIds })}
                      placeholder="Selecione as áreas de conhecimento"
                      entity="área"
                      feminine
                      isDisabled={saving}
                    />
                  </FormField>
                </Box>

                <PostStatusControls
                  isActive={form.is_active}
                  scheduledAt={form.scheduled_at}
                  onActiveChange={(value) => updateForm({ is_active: value })}
                  onScheduledAtChange={(value) => updateForm({ scheduled_at: value })}
                  isDisabled={saving}
                />
              </Flex>
              <Flex direction="column" gap={4} w="25%">
                <Box ref={imageRef} tabIndex={-1}>
                  <ImageCropInput
                    label="Thumbnail"
                    value={form.image ?? existingImageUrl ?? null}
                    onChange={(file) => updateForm({ image: file })}
                    aspectRatio={500 / 350}
                    suggestedSize="500px x 350px"
                    previewAspectRatio="500 / 350"
                    accept="coverImage"
                    maxSizeMB={2}
                    isDisabled={saving}
                    error={formErrors.image ? [formErrors.image] : undefined}
                  />
                  <Text variant="caption" mt={1}>
                    Formatos aceitos: webp, png, jpg, jpeg.
                  </Text>
                </Box>
              </Flex>
            </Flex>
            <Flex direction="column" gap={4}>
              <RichTextField
                ref={bodyRef}
                label="Conteúdo do Post"
                error={formErrors.body}
                isRequired
                toolbar="post"
                minH="300px"
                value={form.body}
                onChange={(body) => updateForm({ body })}
                placeholder="Escreva o conteúdo do post aqui..."
              />
            </Flex>
            <Flex direction="column" gap={4} mt={4}>
              <FormField label="Campos de SEO" isRequired error={formErrors.seo_title}>
                <Box>
                  <FormLabel htmlFor="seo_title">Título SEO</FormLabel>
                  <Input
                    ref={seoTitleRef}
                    value={form.seo_title}
                    onChange={(e) => updateForm({ seo_title: e.target.value })}
                    id="seo_title"
                    disabled={saving}
                    maxLength={LIMITS.seo_title}
                  />
                  <CharCount value={form.seo_title} max={LIMITS.seo_title} />
                </Box>
              </FormField>
              <Box>
                <FormField label="Descrição SEO" htmlFor="meta_description" error={formErrors.meta_description}>
                  <Textarea
                    ref={metaDescriptionRef}
                    id="meta_description"
                    value={form.meta_description}
                    onChange={(e) => updateForm({ meta_description: e.target.value })}
                    disabled={saving}
                    maxLength={LIMITS.meta_description}
                    rows={3}
                  />
                  <CharCount value={form.meta_description} max={LIMITS.meta_description} />
                </FormField>
              </Box>
              <Box>
                <FormField
                  label="Palavras-chave SEO"
                  htmlFor="seo_keywords"
                  error={formErrors.seo_keywords}
                  helperText="Para adicionar várias de uma vez, separe-as com vírgula."
                >
                  <Input
                    ref={seoKeywordsRef}
                    value={tempKeyword}
                    onChange={(e) => setTempKeyword(e.target.value)}
                    placeholder="Digite uma palavra-chave e pressione Enter"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addKeywords()
                      }
                    }}
                    onBlur={addKeywords}
                    id="seo_keywords"
                    disabled={saving}
                  />
                </FormField>
                {form.seo_keywords.length > 0 && (
                  <Wrap mt={2} spacing={2} maxH="250px" overflowY="auto">
                    {form.seo_keywords.map((keyword) => (
                      <WrapItem key={keyword}>
                        <Tag size="md" borderRadius="full" variant="subtle" colorScheme="gray">
                          <TagLabel>{keyword}</TagLabel>
                          <TagCloseButton
                            aria-label={`Remover ${keyword}`}
                            onClick={() => removeKeyword(keyword)}
                            isDisabled={saving}
                          />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}
              </Box>
              <Box>
                <FormField label="Slug SEO" htmlFor="seo_slug" error={formErrors.seo_slug} isRequired>
                  <Input
                    ref={seoSlugRef}
                    value={form.seo_slug}
                    onChange={(e) => updateForm({ seo_slug: e.target.value })}
                    id="seo_slug"
                    disabled={saving}
                    maxLength={LIMITS.seo_slug}
                  />
                  <CharCount value={form.seo_slug} max={LIMITS.seo_slug} />
                </FormField>
              </Box>
              <Box>
                <FormField
                  label="Alt Text da imagem"
                  htmlFor="image_alt_text"
                  error={formErrors.image_alt_text}
                  isRequired
                >
                  <Input
                    ref={imageAltTextRef}
                    value={form.image_alt_text}
                    onChange={(e) => updateForm({ image_alt_text: e.target.value })}
                    id="image_alt_text"
                    disabled={saving}
                    maxLength={LIMITS.image_alt_text}
                  />
                  <CharCount value={form.image_alt_text} max={LIMITS.image_alt_text} />
                </FormField>
              </Box>
            </Flex>
            <Flex direction="column" gap={4} mt={4}>
              <FormField label="Schema Markup" error={formErrors.schema_type}>
                <Box ref={schemaTypeRef} tabIndex={-1}>
                  <FormLabel htmlFor="schema_type">Tipo</FormLabel>
                  <SearchableSelect<'Article' | 'BlogPosting' | 'NewsArticle'>
                    inputId="schema_type"
                    value={form.schema_type}
                    options={schemaTypeOptions}
                    onChange={(schemaType) => schemaType && updateForm({ schema_type: schemaType })}
                    isDisabled={saving}
                    placeholder="Selecione o tipo de schema"
                  />
                </Box>
              </FormField>
              <Box>
                <FormField label="Headline" htmlFor="schema_headline" error={formErrors.schema_headline}>
                  <Input
                    ref={schemaHeadlineRef}
                    id="schema_headline"
                    value={form.schema_headline}
                    onChange={(e) => updateForm({ schema_headline: e.target.value })}
                    disabled={saving}
                    maxLength={LIMITS.schema_headline}
                  />
                  <CharCount value={form.schema_headline} max={LIMITS.schema_headline} />
                </FormField>
              </Box>
              <Box>
                <FormField
                  label="Resumo para Schema"
                  htmlFor="schema_description"
                  error={formErrors.schema_description}
                >
                  <Textarea
                    ref={schemaDescriptionRef}
                    id="schema_description"
                    value={form.schema_description}
                    onChange={(e) => updateForm({ schema_description: e.target.value })}
                    disabled={saving}
                    maxLength={LIMITS.schema_description}
                    rows={3}
                  />
                  <CharCount value={form.schema_description} max={LIMITS.schema_description} />
                </FormField>
              </Box>
            </Flex>
          </>
        )}
      </FormModal>

      <ConfirmDialog
        isOpen={isDiscardOpen}
        onClose={() => setIsDiscardOpen(false)}
        onConfirm={() => {
          setIsDiscardOpen(false)
          onClose()
        }}
        action="delete"
        title="Descartar alterações?"
        description="As alterações feitas neste post ainda não foram salvas e serão perdidas."
        confirmLabel="Descartar"
      />

      {!blogPostId && (
        <DraftsModal isOpen={isDraftsOpen} onClose={() => setIsDraftsOpen(false)} onSelectDraft={handleSelectDraft} />
      )}
    </>
  )
}
